// utils/helpers/helper.generateData.js
import React from 'react';
import moment from 'moment';
// helper
import { STATUSES_LIST, DAY_OF_WEEK, ABS_TYPES_LIST, TYPES_LIST } from 'utils/helpers/helper.constant';
import { sortByKey, getObjId, getArrayIds } from 'utils/helpers/helper.general';
import { getUniqueArrayValue } from 'utils/helpers/helper.filter-sort';
import {
  dateFormat, // DD.MM.YYYY
  dateFormatCompare, // DD-MM-YYYY
} from 'utils/moment/moment.constant';
import {
  secondsToTimeLabel,
  isTimeBetween,
  isDatesBetween,
  isSameDates,
  calculateBusinessDays,
} from 'utils/moment/moment.utils';

export const getAbsenceTypeLabel = type => {
  switch (type) {
    case 'medical':
      return 'médicale';
    case 'no-medical':
      return 'non-médicale';
    case 'sickness':
      return 'maladie';
  }
};

export const getAbsenceStatusLabel = type => {
  switch (type) {
    case 'pending':
      return 'En cours de traitement';
    case 'justification-missing':
      return 'Justificatif à fournir';
    case 'sick':
      return 'Malade';
    case 'sickness-finish':
      return 'Maladie terminée';
    case 'accepted':
      return 'Accepté';
    case 'refused':
      return 'Refusé';
  }
};

export const getStatusesList = type => {
  let tempStatuses = STATUSES_LIST;
  if (type == 'sickness') {
    return tempStatuses.filter(
      elem => elem?.key == 'sickness-finish' || elem?.key == 'sick',
    );
  } else {
    return tempStatuses.filter(
      elem => elem?.key !== 'sickness-finish' && elem?.key !== 'sick',
    );
  }
};

export const getTypeList = () => ABS_TYPES_LIST;

export const getUndertakeLabel = undertake => {
  switch (undertake) {
    case 'with-parent':
      return `L’enfant voyage avec le parent`;
    case 'alone':
      return `L’enfant se déplace seul`;
  }
};

export const getAbsenceReason = element => {
  if (element?.type !== 'sickness') {
    return element?.reason;
  } else {
    return 'Maladie';
  }
};

export const getLevelLabel = number => {
  switch (number) {
    case 5:
      return 'Niveau V';
    case 4:
      return 'Niveau IV';
    case 3:
      return 'Niveau III';
    case 2:
      return 'Niveau II';
    case 1:
      return 'Niveau I';
    case 0:
      return 'Pas de niveau';
  }
};

export const getDayOfWeekObj = dayKey => {
  return DAY_OF_WEEK?.find(el => el?.key == dayKey);
};

export const generateAbsenceReason = absence => {
  if (!!absence && absence?.type == 'sickness') {
    if (absence?.status == 'sick') {
      return (
        <span className="label-tag label-tag--sick">{`Excusé(e) (Maladie)`}</span>
      );
    } else if (absence?.status == 'sickness-finish') {
      return (
        <span className="label-tag label-tag--sick">{`Excusé(e) (Maladie terminée)`}</span>
      );
    }
  } else if ( !!absence ) {
    return (
      <span className={`label-tag label-tag--${absence?.status}`}>
        {`${getAbsenceStatusLabel(absence?.status)} - ${getAbsenceTypeLabel(
          absence?.type,
        )} ${absence?.reason ? `(${absence?.reason})` : ''}`}
      </span>
    );
  }
};

/**
 * Dates & Time
 */

// sort by: week, start, end, totalSec
export const getAbsenceCoursesTime = (element, sort = 'start') => {
  const timeRange = [];

  if (element.hasOwnProperty('courses') && element?.courses.length > 0) {
    element?.courses.map(course => {
      const st = moment(course?.course_time_start)
        .format('HH:mm')
        .toString();
      const fn = moment(course?.course_time_end)
        .format('HH:mm')
        .toString();
      const [sthours, stminutes] = st.split(':');
      const [fnhours, fnminutes] = fn.split(':');

      const startSeconds = +sthours * 60 * 60 + +stminutes * 60;
      const endSeconds = +fnhours * 60 * 60 + +fnminutes * 60;
      const final = endSeconds - startSeconds;

      timeRange.push({
        from: moment(course?.course_time_start, dateFormat),
        to: moment(course?.course_time_end, dateFormat),
        week: course?.course_week,
        start: st,
        end: fn,
        startSec: startSeconds,
        endSec: endSeconds,
        totalSec: final,
        duration: secondsToTimeLabel(final),
      });
    });

    timeRange.sort(sortByKey(sort));
  }
  return timeRange;
};

export const getAbsenceMedMoreRange = (element, sort = 'start') => {
  let timeRange = false;

  if (element) {
    const dateFrom = element?.date_from;
    const timeFrom = element?.time_from;
    const dateTo = element?.date_to;
    const timeTo = element?.time_to;

    let startD = moment(dateFrom);
    let endD = moment(dateTo);

    // Difference in number of days
    const days = calculateBusinessDays(startD, endD);

    const st = moment(timeFrom)
      .format('HH:mm')
      .toString();
    const fn = moment(timeTo)
      .format('HH:mm')
      .toString();
    const [sthours, stminutes] = st.split(':');
    const [fnhours, fnminutes] = fn.split(':');

    const startSeconds = +sthours * 60 * 60 + +stminutes * 60;
    const endSeconds = +fnhours * 60 * 60 + +fnminutes * 60;
    const final = endSeconds - startSeconds;

    timeRange = {
      from: moment(dateFrom).format(dateFormat),
      to: moment(dateTo).format(dateFormat),
      weekStart: moment(dateFrom).format('dd'),
      weekEnd: moment(dateTo).format('dd'),
      days: days,
      start: st,
      end: fn,
      startSec: startSeconds,
      endSec: endSeconds,
      totalSec: final,
      duration: secondsToTimeLabel(final),
    };
  }
  return timeRange;
};

export const getAbsenceCoursesTimeDuration = element => {
  const timeRange = getAbsenceCoursesTime(element);
  const lastIndex = timeRange.length - 1;
  if (timeRange.length == 1) {
    return timeRange[lastIndex]?.duration;
  } else {
    let totalDurationSec = 0;
    timeRange.map(tm => {
      totalDurationSec += Number(tm?.totalSec);
    });
    return secondsToTimeLabel(totalDurationSec);
  }
};

export const getAbsenceCoursesStartDuration = element => {
  let timeRange = getAbsenceCoursesTime(element);

  if (timeRange && timeRange.length > 0) {
    const lastIndex = timeRange.length - 1;
    const firstCourse = timeRange[0];
    let dLabel;
    if (timeRange.length == 1) {
      dLabel = timeRange[lastIndex]?.duration;
    } else {
      let totalDurationSec = 0;
      timeRange.map(tm => {
        totalDurationSec += Number(tm?.totalSec);
      });
      dLabel = secondsToTimeLabel(totalDurationSec);
    }
    return `${firstCourse?.start}${dLabel ? ` (${dLabel})` : ''}`;
  } else {
    return '';
  }
};

export const getAbsenceCoursesDuration = element => {
  let timeRange = getAbsenceCoursesTime(element);

  if (timeRange && timeRange.length > 0) {
    const lastIndex = timeRange.length - 1;
    const firstCourse = timeRange[0];
    let dLabel;
    if (timeRange.length == 1) {
      dLabel = timeRange[lastIndex]?.duration;
    } else {
      let totalDurationSec = 0;
      timeRange.map(tm => {
        totalDurationSec += Number(tm?.totalSec);
      });
      dLabel = secondsToTimeLabel(totalDurationSec);
    }
    return dLabel;
  } else {
    return '';
  }
};

export const getAbsenceDuration = element => {
  if (element?.type == 'sickness' && element?.duration !== 'more') {
    if (element?.duration == 'day') {
      return 'Jour';
    } else {
      return 'Une demi-journée';
    }
  } else if (element?.type == 'sickness' && element?.duration == 'more') {
    return getAbsenceCoursesDuration(element);
  } else if (element?.type !== 'sickness' && element?.duration !== 'more') {
    /*  const recurring = element?.recurring;
    const recurringDate = element?.recurring_date; */
    return getAbsenceCoursesDuration(element);
  } else if (element?.type !== 'sickness' && element?.duration == 'more') {
    const rangeData = getAbsenceMedMoreRange(element);
    return `${
      rangeData?.days !== 0
        ? `${rangeData?.days}j ${rangeData?.duration}`
        : `${rangeData?.duration}`
    }`;
  } else {
    return '';
  }
};

export const getAbsenceStartDuration = element => {
  if (element?.type == 'sickness' && element?.duration !== 'more') {
    if (element?.duration == 'day') {
      return 'Jour';
    } else {
      return 'Une demi-journée';
    }
  } else if (element?.type == 'sickness' && element?.duration == 'more') {
    return getAbsenceCoursesStartDuration(element);
  } else if (element?.type !== 'sickness' && element?.duration !== 'more') {
    /*  const recurring = element?.recurring;
    const recurringDate = element?.recurring_date; */
    return getAbsenceCoursesDuration(element);
  } else if (element?.type !== 'sickness' && element?.duration == 'more') {
    const rangeData = getAbsenceMedMoreRange(element);
    return `${rangeData?.start} ${
      rangeData?.days !== 0
        ? `( ${rangeData?.days}j ${rangeData?.duration})`
        : `(${rangeData?.duration})`
    }`;
  } else {
    return '';
  }
};

export const getAbsenceStartTime = element => {
  if (
    (element?.type == 'sickness' && element?.duration == 'more') ||
    (element?.type !== 'sickness' && element?.duration !== 'more')
  ) {
    let timeRange = getAbsenceCoursesTime(element);

    if (timeRange && timeRange.length > 0) {
      const lastIndex = timeRange.length - 1;
      const firstCourse = timeRange[0];
      let dLabel;
      if (timeRange.length == 1) {
        dLabel = timeRange[lastIndex]?.duration;
      } else {
        let totalDurationSec = 0;
        timeRange.map(tm => {
          totalDurationSec += Number(tm?.totalSec);
        });
        dLabel = secondsToTimeLabel(totalDurationSec);
      }
      return `${firstCourse?.start}`;
    } else {
      return '';
    }
  } else if (element?.type !== 'sickness' && element?.duration == 'more') {
    const rangeData = getAbsenceMedMoreRange(element);
    return `${rangeData?.start}`;
  } else {
    return '';
  }
};

export const getAbsenceMainDate = element => {
  let mainDate;
  if (element?.duration == 'more' && element?.type !== 'sickness') {
    mainDate = element?.date_from;
  } else {
    mainDate = element?.date;
  }
  return mainDate;
};

// TODO: add calcutaion of time for duration
export const getExcusedAbsenceStd = (dataSource, onDate, course) => {
  const courseId = course?._id;
  const courseStartTime = course?.course_time_start;
  const courseEndTime = course?.course_time_end;
  const studentList = dataSource ? dataSource : course?.students;

  let excusedStds = [];
  if (studentList && onDate && course) {
    excusedStds =
      studentList &&
      studentList.length > 0 &&
      studentList
        .map(std => {
          if (std?.absences && std?.absences.length > 0) {
            const hasAbs = std?.absences?.filter(abs => {
              if ( abs?.status !== 'sickness-finish' &&  abs?.status !== 'accepted' ) {
                if (abs?.type == 'sickness' && abs?.duration !== 'more') {
                  const sameDate = isSameDates(onDate, abs?.date);
                  if (sameDate) return abs;
                } else if (
                  (abs?.type == 'sickness' && abs?.duration == 'more') ||
                  (abs?.type !== 'sickness' && abs?.duration !== 'more')
                ) {
                  const sameDate = isSameDates(onDate, abs?.date);
                  const sameCourse =
                    abs?.courses &&
                    abs?.courses.length > 0 &&
                    abs?.courses.some(c => getObjId(c) == courseId);

                  if (
                    (sameDate && sameCourse) ||
                    (sameDate && abs?.courses.length == 0)
                  ) {
                    return abs;
                  }
                } else if (
                  abs?.type !== 'sickness' &&
                  abs?.duration == 'more'
                ) {

                  const absSameDate = isSameDates(abs?.date_from, abs?.date_to);
                  const sameDate = isDatesBetween(
                    abs?.date_from,
                    abs?.date_to,
                    onDate,
                  );

                  const compareTimeFromDay = moment(abs?.time_from).endOf(
                    'day',
                  );
                  const compareTimeToDay = moment(abs?.time_from).startOf(
                    'day',
                  );

                  let sameTimeFrom, sameTimeTo;
                  if (absSameDate) {
                    sameTimeFrom = isTimeBetween(
                      moment(abs?.time_from).format('HH:mm'),
                      moment(abs?.time_to).format('HH:mm'),
                      moment(courseStartTime).format('HH:mm'),
                    );
                    sameTimeTo = isTimeBetween(
                      moment(abs?.time_from).format('HH:mm'),
                      moment(abs?.time_to).format('HH:mm'),
                      moment(courseEndTime).format('HH:mm'),
                    );
                  } else {
                    sameTimeFrom = isTimeBetween(
                      moment(abs?.time_from).format('HH:mm'),
                      compareTimeFromDay.format('HH:mm'),
                      moment(courseStartTime).format('HH:mm'),
                    );
                    sameTimeTo = isTimeBetween(
                      compareTimeToDay.format('HH:mm'),
                      moment(abs?.time_to).format('HH:mm'),
                      moment(courseEndTime).format('HH:mm'),
                    );
                  }

                  if (sameDate && (sameTimeFrom || sameTimeTo)) {
                    return abs;
                  }
                }
              }
            }) || [];

            return hasAbs && hasAbs.length > 0
              ? { ...std, hasAbs: hasAbs }
              : { ...std, hasAbs: [] };
          } else {
            return null;
          }
        })
        .filter(el => el !== null);
  }
  return excusedStds ? excusedStds : [];
};

export const getExcused = (dataSource, onDate, course) => {
  const studentList = dataSource ? dataSource : course?.students;
  let excused = [];
  if (studentList && onDate && course) {
    excused = getExcusedAbsenceStd(studentList, onDate, course) || [];
  
    if (course?.attendances && course?.attendances.length > 0) {
      const currentAttendanceArr = getAttendancesOnDate(course, onDate);

      const missingStudents = currentAttendanceArr[0]?.missing_students
        ? currentAttendanceArr[0]?.missing_students
        : [];
    
      excused = [...new Set([...excused, ...missingStudents])];
    }
  }
  return excused;
};

export const getExcusedAbsences = (dataSource, onDate, course) => {
  const courseId = course?._id;
  const courseStartTime = course?.course_time_start;
  const courseEndTime = course?.course_time_end;

  if (dataSource && onDate && course) {
    const excusedAbsences = [];
    const map = new Map();

    dataSource &&
      dataSource.map(std => {
        if (std?.absences && std?.absences.length > 0) {
          const hasAbs = std?.absences.filter(abs => {
            if (
              abs?.status !== 'sickness-finish' ||
              abs?.status !== 'accepted'
            ) {
              if (abs?.type == 'sickness' && abs?.duration !== 'more') {
                const sameDate = isSameDates(onDate, abs?.date);
                if (sameDate) return abs;
              } else if (
                (abs?.type == 'sickness' && abs?.duration == 'more') ||
                (abs?.type !== 'sickness' && abs?.duration !== 'more')
              ) {
                const sameDate = isSameDates(onDate, abs?.date);

                const sameCourse =
                  abs?.courses &&
                  abs?.courses.length > 0 &&
                  abs?.courses.some(c => c == courseId);

                if (
                  (sameDate && sameCourse) ||
                  (sameDate && abs?.courses.length == 0)
                ) {
                  return abs;
                }
              } else if (abs?.type !== 'sickness' && abs?.duration == 'more') {
                const absSameDate = isSameDates(abs?.date_from, abs?.date_to);
                const sameDate = isDatesBetween(
                  abs?.date_from,
                  abs?.date_to,
                  onDate,
                );

                const compareTimeFromDay = moment(abs?.time_from).endOf('day');
                const compareTimeToDay = moment(abs?.time_from).startOf('day');

                let sameTimeFrom, sameTimeTo;
                if (absSameDate) {
                  sameTimeFrom = isTimeBetween(
                    moment(abs?.time_from).format('HH:mm'),
                    moment(abs?.time_to).format('HH:mm'),
                    moment(courseStartTime).format('HH:mm'),
                  );
                  sameTimeTo = isTimeBetween(
                    moment(abs?.time_from).format('HH:mm'),
                    moment(abs?.time_to).format('HH:mm'),
                    moment(courseEndTime).format('HH:mm'),
                  );
                } else {
                  sameTimeFrom = isTimeBetween(
                    moment(abs?.time_from).format('HH:mm'),
                    compareTimeFromDay.format('HH:mm'),
                    moment(courseStartTime).format('HH:mm'),
                  );
                  sameTimeTo = isTimeBetween(
                    compareTimeToDay.format('HH:mm'),
                    moment(abs?.time_to).format('HH:mm'),
                    moment(courseEndTime).format('HH:mm'),
                  );
                }

                if (sameDate && (sameTimeFrom || sameTimeTo)) {
                  return abs;
                }
              }
            }
          });

          map.set(hasAbs, true);
          excusedAbsences.push(...hasAbs);
          return hasAbs && hasAbs.length > 0 ? hasAbs : null;
        } else {
          return null;
        }
      });

    return excusedAbsences;
  } else {
    return [];
  }
};

export const getAttendancesOnDate = (course, onDate) => {
  let attendancesArr = [];
  if (course && onDate) {
    attendancesArr =
      course?.attendances &&
      course?.attendances.length > 0 &&
      course?.attendances.filter(att => {
        return (
          isSameDates(onDate, att?.date) &&
          getObjId(att?.course) == getObjId(course) &&
          att
        );
      });
  }
  return attendancesArr ? attendancesArr : [];
};

/* Communication */

export const getCommunicationItemStatusClass = communication => {
  let classStatus = '';

  if (communication?.status != 'draft') {
    if (communication?.types && communication?.types.length > 0) {
      const uniqueType = getUniqueArrayValue(communication?.types);
      if (
        uniqueType.includes('appointments') ||
        uniqueType.includes('forms')
      ) {
        classStatus = 'status__attachments';
      } else if (uniqueType.includes('no-type')) {
        classStatus = 'status__no-type';
      } else if (uniqueType.includes('documents')) {
        classStatus = 'status__documents';
      } else if (uniqueType.includes('discussion')) {
        classStatus = 'status__discussion';
      }
    } else {
      classStatus = 'status__no-type';
    }
  }
  return `${communication?.status} ${classStatus}`;
};

export const getCommunicationItemStatus = communication => {
  switch (communication?.status) {
    case 'draft':
      return 'Brouillon';

    case 'sent':
      if (communication?.types && communication?.types.length > 0) {
        const uniqueType = getUniqueArrayValue(communication?.types);
        if (
          uniqueType.includes('appointments') ||
          uniqueType.includes('forms')
        ) {
          return `Envoyé – Réponses : ${communication?.studentsAnsweredAmount || communication?.studentsAnswered?.length || 0}/${communication?.studentsAmount ||
            0}`;
        } else if (
          uniqueType.includes('no-type') ||
          uniqueType.includes('documents')
        ) {
          return `Envoyé – Ouverture : ${communication?.studentsReadAmount || 0}/${communication?.studentsAmount ||
            0}`;
        }
      } else {
        return `Envoyé – Ouverture : ${communication?.studentsReadAmount || 0}/${communication?.studentsAmount ||
          0}`;
      }
  }
};

export const getCommunicationStatusForFormAppoint = communication => {
  if (communication?.types && communication?.types.length > 0) {
    const uniqueType = getUniqueArrayValue(communication?.types);
    if (
      uniqueType.includes('appointments') ||
      uniqueType.includes('forms')
    ) {
      return `Envoyé – Réponses : ${communication?.studentsAnsweredAmount || communication?.studentsAnswered?.length || 0}/${communication?.studentsAmount ||
        0}`;
    } 
  } else {
    return '';
  }
};

export const getCommunicationType = communication => {
  return (
    (communication?.types && getUniqueArrayValue(communication?.types)) || []
  );
};

export const getCommunicationTypeLabel = typeKey => {
  // TYPES_LIST
  const resultObj = TYPES_LIST.find(el => (el?.key || el?.velue) == typeKey);
  return resultObj || {};
};

export const getCommunicationAudienceAsString = communication => {
  let audienceArray = [];
  if (communication?.audience) {
    if (communication?.audience?.school) {
      audienceArray.push(`Toute l'école`);
    } else {
      if (communication?.audience?.classes) {
        communication?.audience?.classes?.map(classItem => {
          classItem?.title && audienceArray.push(classItem?.title);
        });
      }
      if (communication?.audience?.courses) {
        communication?.audience?.courses?.map(courseItem => {
          if (courseItem?._id) {
            const class_info = Array.isArray(courseItem?.class_info)
              ? courseItem?.class_info[0]
              : courseItem?.class_info;
            const courseName = `${courseItem?.subject?.name} • ${
              class_info?.title
            }`;
            audienceArray.push(courseName);
          }
        });
      }
      if (communication?.audience?.students) {
        communication?.audience?.students?.map(stdItem => {
          if (stdItem?._id) {
            const stdName = `${stdItem?.description?.lastname} ${
              stdItem?.description?.firstname
            }`;
            audienceArray.push(stdName);
          }
        });
      }
      if (communication?.audience?.users) {
        communication?.audience?.users?.map(stdItem => {
          if (stdItem?._id) {
            const stdName = `${stdItem?.description?.lastname} ${
              stdItem?.description?.firstname
            }`;
            audienceArray.push(stdName);
          }
        });
      }
      if (communication?.audience?.audiences) {
        communication?.audience?.audiences?.map(classItem => {
          classItem?.title && audienceArray.push(classItem?.title);
        });
      }
    }
  }
  return audienceArray.length > 0 ? audienceArray.join(', ') : '';
};

export const generateAudienceIncludes = initVal => {
  let resArr = [];
  if (initVal && Object.keys(initVal).length > 0 && initVal?.audience) {
    const audience = initVal?.audience;
    const classes = (audience?.classes && getArrayIds(audience?.classes)) || [];
    const courses = (audience?.courses && getArrayIds(audience?.courses)) || [];
    const users = (audience?.users && getArrayIds(audience?.users)) || [];
    const audiences = (audience?.audiences && getArrayIds(audience?.audiences)) || [];
    const students =
      (audience?.students && getArrayIds(audience?.students)) || [];
    resArr = classes && classes.length > 0 ? resArr.concat(classes) : resArr;
    resArr = courses && courses.length > 0 ? resArr.concat(courses) : resArr;
    resArr = students && students.length > 0 ? resArr.concat(students) : resArr;
    resArr = users && users.length > 0 ? resArr.concat(users) : resArr;
    resArr = audiences && audiences.length > 0 ? resArr.concat(audiences) : resArr;
  } else if (initVal && Object.keys(initVal).length > 0) {
    const classes = (initVal?.classes && getArrayIds(initVal?.classes)) || [];
    const courses = (initVal?.courses && getArrayIds(initVal?.courses)) || [];
    const users = (initVal?.users && getArrayIds(initVal?.users)) || [];
    const audiences = (initVal?.audiences && getArrayIds(initVal?.audiences)) || [];
    const students =
      (initVal?.students && getArrayIds(initVal?.students)) || [];
    resArr = classes && classes.length > 0 ? resArr.concat(classes) : resArr;
    resArr = courses && courses.length > 0 ? resArr.concat(courses) : resArr;
    resArr = users && users.length > 0 ? resArr.concat(users) : resArr;
    resArr = audiences && audiences.length > 0 ? resArr.concat(audiences) : resArr;
    resArr = students && students.length > 0 ? resArr.concat(students) : resArr;
  }
  return resArr || [];
};

export const getResponseNeedAnswerTypes = function(types, answer) {
  const hasRequiredTypes =
    Array.isArray(types) &&
    types?.some((el) => el == "forms" || el == "appointments");
  
  let needAnswers = types?.filter((el) => el == "forms" || el == "appointments") || [];

  let result = {
    actionRequired: !!hasRequiredTypes && answer === null,
    needAnswers: [],
    hasAnswers: [],
  };

  if ( !!hasRequiredTypes && answer != null ) { 
    const answerTypes = Object.keys(answer);

    if (!!answerTypes) {
      needAnswers =
        Array.isArray(types) &&
        types?.length > 0 &&
        types.filter(
          (el) =>
            (el == "forms" || el == "appointments") &&
            !answerTypes.includes(el)
        );
    }

    result = {
      needAnswers: needAnswers,
      hasAnswers: answerTypes,
      actionRequired: needAnswers?.length > 0
    }

  } else if (!!hasRequiredTypes) {
    result = {
      needAnswers: needAnswers,
      hasAnswers: [],
      actionRequired: !!hasRequiredTypes && answer === null
    }
  }
  
  return result;
}